import { useState, useEffect } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Loader2, MapPin } from "lucide-react";

const euro = (n) => new Intl.NumberFormat("fr-FR").format(n) + " €";

// Palette heatmap selon le prix au m²
function tier(price) {
  if (price >= 5000) return { color: "#7c2d12", fill: "#dc2626", label: "5 000 € et +" };
  if (price >= 3500) return { color: "#9a3412", fill: "#ea580c", label: "3 500 – 5 000 €" };
  if (price >= 2500) return { color: "#1e3a8a", fill: "#2563EB", label: "2 500 – 3 500 €" };
  if (price >= 1800) return { color: "#155e75", fill: "#0891b2", label: "1 800 – 2 500 €" };
  return { color: "#166534", fill: "#16a34a", label: "moins de 1 800 €" };
}

const LEGEND = [
  { c: "#dc2626", t: "5 000 € et +" },
  { c: "#ea580c", t: "3 500 – 5 000 €" },
  { c: "#2563EB", t: "2 500 – 3 500 €" },
  { c: "#0891b2", t: "1 800 – 2 500 €" },
  { c: "#16a34a", t: "moins de 1 800 €" },
];

function radius(price) {
  return Math.max(10, Math.min(30, 8 + price / 320));
}

export const PriceMap = ({ height = "560px" }) => {
  const [cities, setCities] = useState(null);

  useEffect(() => {
    api.get("/prices/map").then((r) => setCities(r.data)).catch(() => setCities([]));
  }, []);

  if (!cities) {
    return (
      <div className="flex items-center justify-center rounded-2xl bg-slate-100" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-4 gap-5" data-testid="price-map">
      <div className="lg:col-span-3 rounded-2xl overflow-hidden ring-1 ring-slate-200 shadow-sm" style={{ height }}>
        <MapContainer center={[45.55, 4.9]} zoom={7} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          {cities.map((c) => {
            const t = tier(c.price);
            return (
              <CircleMarker
                key={c.key}
                center={[c.lat, c.lng]}
                radius={radius(c.price)}
                pathOptions={{ color: t.color, fillColor: t.fill, fillOpacity: 0.75, weight: 2 }}
              >
                <Tooltip direction="top" offset={[0, -4]} opacity={1}>
                  <b>{c.city}</b> — {euro(c.price)}/m²
                </Tooltip>
                <Popup>
                  <div style={{ textAlign: "center" }}>
                    <b>{c.city}</b>
                    <div style={{ fontSize: 18, fontWeight: 800, color: t.fill }}>{euro(c.price)}/m²</div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Légende — prix au m²</div>
          <div className="space-y-2">
            {LEGEND.map((l) => (
              <div key={l.t} className="flex items-center gap-2 text-sm text-slate-600">
                <span className="w-3.5 h-3.5 rounded-full" style={{ background: l.c }} />
                {l.t}
              </div>
            ))}
          </div>
          <p className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-400 leading-relaxed">
            Prix moyens indicatifs au m² (appartements), établis d'après les données de marché
            (SeLoger) et les ventes réelles (DVF). Mis à jour {new Date().getFullYear()}.
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm max-h-64 overflow-y-auto">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Classement</div>
          <ul className="space-y-1.5">
            {cities.map((c, i) => (
              <li key={c.key} className="flex justify-between text-sm" data-testid={`map-city-${c.key}`}>
                <span className="text-slate-600">{i + 1}. {c.city}</span>
                <span className="font-semibold text-slate-900">{euro(c.price)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default function MapPage() {
  return (
    <div className="App min-h-screen bg-slate-50" data-testid="map-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="max-w-2xl mb-8">
          <div className="flex items-center gap-2 text-[#2563EB] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            <MapPin className="w-4 h-4" /> Carte des prix
          </div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tighter font-extrabold text-slate-900">
            Le prix au m² en Auvergne-Rhône-Alpes
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            Explorez les prix moyens au mètre carré dans plus de 30 villes de la région, alignés sur
            les données SeLoger et les ventes réelles (DVF). Cliquez sur un point pour voir le détail.
          </p>
        </div>
        <PriceMap />
      </div>
      <Footer />
    </div>
  );
}
